import React, { useState, MouseEvent, JSX } from 'react';
import AwesomeSlider from 'react-awesome-slider';
// eslint-disable-next-line
const withAutoplay = require('react-awesome-slider/dist/autoplay').default;
// eslint-disable-next-line
const YouTube = require('react-youtube').default;

import { ApiImage, ApiMedia } from '@ibe/api';
import useMediaQuery, { MEDIAQUERY_DEFAULTS, QueryOpts } from '../../../Hooks/useMediaQuery';
import { getImageUrl } from '../../../Modules/Camper/externals';

const AutoplaySlider = withAutoplay(AwesomeSlider);

export interface Props {
  images?: string[];
  videos?: string[];
  placeholderImage: string;
  fixedRatio?: {
    x: number;
    y: number;
    mediaQueryOptions?: QueryOpts;
  };
  mobileControls?: boolean;
  smallControls?: boolean;
  autoplay?: boolean;
  autoplayInterval?: number;
  rawComponents?: JSX.Element[];
  checkProtocol?: boolean;
  containImage?: boolean;
  buttonContentRight?: React.ReactNode;
  buttonContentLeft?: React.ReactNode;
  organicArrows?: boolean;
}

export const Slider = (props: Props): JSX.Element => {
  const {
    images,
    videos,
    placeholderImage,
    fixedRatio,
    mobileControls,
    smallControls,
    autoplay = true,
    autoplayInterval,
    rawComponents,
    containImage,
    buttonContentRight,
    buttonContentLeft,
    organicArrows
  } = props;

  const [autoPlay, setAutoPlay] = useState(autoplay);

  const isBreakpoint = useMediaQuery({
    type: fixedRatio?.mediaQueryOptions?.type || 'min',
    query: fixedRatio?.mediaQueryOptions?.query || MEDIAQUERY_DEFAULTS.xs
  });

  const setAutoPlayFalse = (): void => {
    setAutoPlay(false);
  };

  function getYoutubeId(src: string): string {
    let id = '';
    const url = src.replace(/(>|<)/gi, '').split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/);
    if (url[2] !== undefined) {
      const splitUrl = url[2].split(/[^0-9a-z_-]/i);
      id = splitUrl[0];
    } else {
      id = src;
    }
    return id;
  }

  let medias: JSX.Element[] = [];
  if (images && images.length) {
    const imageDivs = images.map(src => {
      return (
        <div
          key={src}
          style={{ backgroundImage: `url("${src}")` }}
          className={containImage ? 'slider__screenImageContain' : 'slider__screenImage'}
        />
      );
    });
    medias = medias.concat(imageDivs);
  }
  if (videos && videos.length) {
    const mp4Videos = videos
      .filter(v => v.includes('.mp4'))
      .map(video => (
        <div key={video} className="slider__screen">
          <video width="100%" height="100%" controls onPlay={(): void => setAutoPlayFalse()}>
            <source src={video} type="video/mp4" style={{ width: '100%', height: '100%' }} />
          </video>
        </div>
      ));
    const youtubeVideosId = videos
      .filter(v => v.includes('youtu'))
      .map(video => (
        <div key={video} className="slider__screen">
          <YouTube
            videoId={getYoutubeId(video)}
            onPlay={(): void => setAutoPlayFalse()}
            opts={{ width: '100%', height: '100%' }}
          />
        </div>
      ));
    medias = medias.concat(mp4Videos);
    medias = medias.concat(youtubeVideosId);
  }
  if (!medias.length) {
    medias.push(
      <div
        key={0}
        style={{ backgroundImage: `url("${placeholderImage}")` }}
        className="slider__screenImage"
      />
    );
  }

  if (rawComponents && rawComponents.length) {
    medias = rawComponents;
  }

  return (
    <div
      className={`slider__layoutContainer${
        fixedRatio && isBreakpoint ? ' slider__layoutContainer--fixed' : ''
      }${mobileControls ? ' slider__layoutContainer--mobile-controls' : ''}${
        smallControls ? ' slider__layoutContainer--small-controls' : ''
      }`}
      style={
        fixedRatio && isBreakpoint
          ? { paddingTop: `${(fixedRatio.y / fixedRatio.x) * 100}%` }
          : undefined
      }
      onClick={(e: MouseEvent<HTMLDivElement>): void => e.stopPropagation()}
    >
      <AutoplaySlider
        className="slider__awesomeSlider"
        play={autoPlay}
        infinite
        interval={autoplayInterval || 3000}
        bullets={false}
        cancelOnInteraction
        fillParent
        mobileTouch
        organicArrows={organicArrows}
        buttonContentRight={buttonContentRight}
        buttonContentLeft={buttonContentLeft}
      >
        {medias &&
          medias.length > 0 &&
          medias.map(screen => (
            <div key={screen.toString()} className="slider__screen">
              <div>{screen}</div>
            </div>
          ))}
      </AutoplaySlider>
    </div>
  );
};

export function withMedia(
  SliderComponent: (props: Props) => JSX.Element,
  placeholderImage: string,
  media: ApiMedia | undefined,
  props?: Partial<Props>,
  imageSize?: 'small' | 'large'
): JSX.Element {
  return SliderComponent({
    ...splitMediaIntoImagesAndVideosUrls(media, imageSize, props?.checkProtocol),
    placeholderImage,
    ...props
  });
}

export function splitMediaIntoImagesAndVideosUrls(
  props?: ApiMedia,
  imageSize?: 'small' | 'large',
  checkProtocol?: boolean
): { images: string[]; videos: string[] } {
  const images: string[] = [];
  const videos: string[] = [];
  if (props) {
    let mainImg: string | undefined;
    if (props.mainImage) {
      mainImg = props.mainImage?.large?.url || props.mainImage?.small?.url;
      if (checkProtocol) {
        mainImg = getImageUrl(mainImg);
      }
    }
    props.images.forEach(img => {
      if ((!imageSize || imageSize === 'large') && img.large && img.large.url) {
        images.push(checkProtocol ? (getImageUrl(img.large.url) as string) : img.large.url);
      } else if (imageSize === 'small' && img.small && img.small.url) {
        images.push(checkProtocol ? (getImageUrl(img.small.url) as string) : img.small.url);
      }
    });
    if (mainImg) {
      images.unshift(mainImg);
    }
    props.videos.forEach(video => {
      if (video.url) {
        videos.push(video.url);
      }
    });
  }
  return { images, videos };
}

export function splitMediaIntoImagesAndVideos(
  props?: ApiMedia,
  imageSize?: 'small' | 'large'
): { images: ApiImage[]; videos: string[] } {
  const images: ApiImage[] = [];
  const videos: string[] = [];
  if (props) {
    let mainImg: ApiImage | undefined;
    if (props.mainImage) {
      mainImg = props.mainImage?.large || props.mainImage?.small;
    }
    props.images.forEach(img => {
      if ((!imageSize || imageSize === 'large') && img.large && img.large.url) {
        images.push(img.large);
      } else if (imageSize === 'small' && img.small && img.small.url) {
        images.push(img.small);
      }
    });
    if (mainImg) {
      images.unshift(mainImg);
    }
    props.videos.forEach(video => {
      if (video.url) {
        videos.push(video.url);
      }
    });
  }
  return { images, videos };
}
